import React, {
  useMemo,
  useContext,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import { Button, Loader, Modal } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import useInfinitePagination from 'hooks/useInfinitePagination';
import { Option } from 'models/Global';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import SelectField from 'components/SelectField';
import bus, { StandardEventTypes } from 'modules/bus';
import { Field, withTypes } from 'react-final-form';
import { UserConnection } from 'models/Message';
import validations from 'validations';
import api from 'api';
import MessagingContext from '../../../providers/Messaging/Messaging.context';
import { getIsArrayEmpty } from 'utils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { DEFAULT_LIMIT } from 'data/global';
import {
  ModalProps,
  ModalRef,
} from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';
import useMessageRepresented from '../../../hooks/useMessageRepresented';

import './NewMessageModal.styles.scss';

const SEPARATOR = ' - ';

type NewMessageFormType = {
  sessionId: string;
};

const NewMessageModal: React.ForwardRefRenderFunction<ModalRef, ModalProps> = (
  props,
  ref,
) => {
  const { className, ...rest } = props;

  const { popServerError } = useContext(PopNotificationsContext);
  const { isSuperAdmin } = useContext(CurrentUserContext);
  const { sessionId, messagingPortfolioProps } = useContext(MessagingContext);

  const [modal, modalRef] = useCallbackRef<ModalRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const open = modal?.openState;

  const { Form } = withTypes<NewMessageFormType>();

  const classes = classNames('new-message-modal', className);

  const getRepresentativeInfo = useMessageRepresented();

  const { t } = useTranslation();

  const {
    loading: loadingConnections,
    items: connections,
    setSearchString,
    onContainerScrolled: onSellersScroll,
  } = useInfinitePagination<UserConnection>({
    condition: open,
    resetDeps: [open],
    dependencies: [open],
    onResetDeps: () => setSearchString(''),
    makeRequest: async (currentPage: number, searchString: string) => {
      return api.messages
        .getUserConnections({
          $page: currentPage.toString(),
          $limit: DEFAULT_LIMIT,
          $sort: '-createdAt',
          $searchString: searchString,
          listingId: messagingPortfolioProps?.listingId,
        })
        .then((res) => res.data);
    },
  });

  const doesntHaveOptions = !loadingConnections && getIsArrayEmpty(connections);

  const mappedConnections = useMemo(
    () =>
      connections
        ?.map((connection) => {
          const { session } = connection;

          const { _id, isDirectMessage } = session || {};

          const { interlocutorFullName, representerName, dealName } =
            getRepresentativeInfo(session);

          if (isDirectMessage) {
            return {
              value: _id,
              isDirectMessage,
              interlocutorName: interlocutorFullName,
              dealName: '',
            };
          }

          const interlocutorName = representerName
            ? representerName + SEPARATOR
            : '';

          return {
            value: _id,
            interlocutorName,
            dealName,
          };
        })
        .sort((a, b) => {
          const aHasInterlocutor = !!a.interlocutorName;
          const bHasInterlocutor = !!b.interlocutorName;

          if (aHasInterlocutor && !bHasInterlocutor) return -1;
          if (!aHasInterlocutor && bHasInterlocutor) return 1;

          if (aHasInterlocutor && bHasInterlocutor) {
            return a.interlocutorName.localeCompare(b.interlocutorName);
          }

          return a.dealName.localeCompare(b.dealName);
        })
        .map(({ value, isDirectMessage, interlocutorName, dealName }) => {
          return {
            value,
            label: isDirectMessage
              ? interlocutorName
              : [interlocutorName, dealName].filter(Boolean).join(' '),
          } as Option;
        }),
    [connections, getRepresentativeInfo],
  );

  const onSubmit = useCallback(
    async ({ sessionId: sessionIdVal }: NewMessageFormType) => {
      try {
        // if current session is same as picked one, don't update it
        if (sessionId === sessionIdVal) return;

        const connection = connections.find(
          ({ session }) => session._id === sessionIdVal,
        );

        bus.broadcastEvent(
          StandardEventTypes.SESSION_PICKED,
          connection.session,
        );
      } catch (e) {
        popServerError(e);
      } finally {
        modal?.close();
      }
    },
    [sessionId, connections, popServerError, modal],
  );

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={t('composeNewMessage')}
      {...rest}
    >
      <Form onSubmit={onSubmit}>
        {({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <p>
              {t(
                doesntHaveOptions
                  ? 'noRecipientsToSelectFrom'
                  : isSuperAdmin
                  ? 'composeNewMessageDescriptionBM'
                  : 'composeNewMessageDescription',
              )}
            </p>

            <div className="select-wrapper">
              {loadingConnections && <Loader overlay={false} />}

              <Field
                name="sessionId"
                async
                options={mappedConnections}
                loading={loadingConnections}
                component={SelectField}
                searchable
                placeholder={t('chooseRecipient')}
                onScroll={onSellersScroll}
                validate={validations.required()}
                onSearchChange={(event: any) => {
                  setSearchString(event.target.value);
                }}
              />
            </div>

            <div className="actions">
              <Button variant="link" onClick={modal?.close}>
                {t('cancel')}
              </Button>
              <Button type="submit">{t('confirm')}</Button>
            </div>
          </form>
        )}
      </Form>
    </Modal>
  );
};

export default forwardRef(NewMessageModal);
